/**
 * Root providers (mirrors web/src/app/providers.tsx).
 *
 * Order matters:
 *   <ThemeProvider>                 — applies .dark class before paint
 *     <ClerkProvider>               — Clerk session; installs the token
 *                                     provider used by the API client
 *       <QueryClientProvider>       — TanStack Query server-state
 *         <TooltipProvider>         — Radix tooltip context
 *           <Toaster/>              — global toast viewport
 *             <children/>
 *
 * The Clerk token provider is wired here so every authenticated request
 * obtains a fresh session JWT at request time (never stored in module state).
 */
import type { ReactNode } from 'react';
import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@/components/theme/ThemeProvider';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/toaster';
import { createQueryClient } from './queryClient';
import { configureSessionTokenProvider } from '@/api/client';
import { loadConfig } from '@/config/env';

const queryClient = createQueryClient();

/** Wires Clerk's `getToken` into the API client once Clerk is mounted. */
function ClerkTokenBridge({ children }: { children: ReactNode }) {
  const { getToken } = useAuth();
  // Install the token provider eagerly on mount. getToken refreshes short-lived
  // JWTs automatically; calling it at request time always yields a fresh token.
  configureSessionTokenProvider(async () => {
    try {
      return (await getToken()) ?? null;
    } catch {
      return null;
    }
  });
  return <>{children}</>;
}

export function AppProviders({ children }: { children: ReactNode }) {
  const { clerkPublishableKey } = loadConfig();
  return (
    <ThemeProvider>
      <ClerkProvider publishableKey={clerkPublishableKey}>
        <ClerkTokenBridge>
          <QueryClientProvider client={queryClient}>
            <TooltipProvider delayDuration={200}>
              {children}
              <Toaster />
            </TooltipProvider>
          </QueryClientProvider>
        </ClerkTokenBridge>
      </ClerkProvider>
    </ThemeProvider>
  );
}
